import { z } from 'zod/v4';

import type { InfinexMessageNamespace } from './base';
import { generateEventSchema } from './schemaBuilder';

export const SessionChangeMethods = [
  'server-disconnected',
  'emit-event',
  'initial-provider-events',
  'request-disconnect',
] as const;
export type SessionChangeMethod = (typeof SessionChangeMethods)[number];
const namespace: InfinexMessageNamespace = 'session-change';

export const emitEventSchema = generateEventSchema({
  namespace,
  method: 'emit-event',

  requestData: z.never(),
  responseData: z.never(),
  notificationData: z
    .object({
      eventName: z.string(),
      eventData: z.any(),
      chainKey: z.string().optional(),
    })
    .strict(),
});

export const initialProviderEvents = generateEventSchema({
  namespace,
  method: 'initial-provider-events',

  requestData: z.null(),
  responseData: z.array(
    z.object({
      eventName: z.string(),
      eventData: z.any(),
    })
  ),
  notificationData: z.never(),
});

export const serverDisconnectedEvent = generateEventSchema({
  namespace,
  method: 'server-disconnected',

  // disallow request / response
  requestData: z.never(),
  responseData: z.never(),
  notificationData: z.null(),
});

export const requestDisconnectEvent = generateEventSchema({
  namespace,
  method: 'request-disconnect',

  requestData: z.null(),
  responseData: z.boolean(),
  notificationData: z.never(),
});

export const tabCheckinEvent = generateEventSchema({
  namespace,
  method: 'tab-checkin',

  requestData: z.any(),
  responseData: z.any(),
  notificationData: z.never(),
});

export const getProviderState = generateEventSchema({
  namespace,
  method: 'get-provider-state',

  requestData: z.any(),
  responseData: z.any(),
  notificationData: z.never(),
});

/* ─────────────────── Aggregated schema arrays ─────────────────── */
export const sessionChangeRequestParamSchemas = [
  requestDisconnectEvent.requestParamsSchema,
  initialProviderEvents.requestParamsSchema,
  tabCheckinEvent.requestParamsSchema,
  getProviderState.requestParamsSchema,
] as const;

export const sessionChangeResponseParamSchemas = [
  requestDisconnectEvent.responseParamsSchema,
  initialProviderEvents.responseParamsSchema,
  tabCheckinEvent.responseParamsSchema,
  getProviderState.responseParamsSchema,
] as const;

export const sessionChangeNotificationParamSchemas = [
  emitEventSchema.notificationParamsSchema,
] as const;

export const sessionChangeRequestEventSchemas = [
  requestDisconnectEvent.requestSchema,
  initialProviderEvents.requestSchema,
  tabCheckinEvent.requestSchema,
  getProviderState.requestSchema,
] as const;

export const sessionChangeResponseEventSchemas = [
  requestDisconnectEvent.responseSchema,
  initialProviderEvents.responseSchema,
  tabCheckinEvent.responseSchema,
  getProviderState.responseSchema,
] as const;

export const sessionChangeNotificationEventSchemas = [
  emitEventSchema.notificationSchema,
] as const;
